/**
 * Omnibar in-flight drafts (localStorage)
 *
 * Persists what the user has typed into the omnibar so a refresh,
 * accidental close, or navigation away doesn't lose composition. Drafts
 * have their own lifecycle — they're per-mode, debounce-saved on every
 * keystroke, and cleared after a successful submit.
 *
 * Storage layout (single localStorage key, JSON-encoded):
 *
 *   {
 *     add?: { doc: TiptapDocument, updatedAt: ISOString },
 *     // ask is intentionally absent — Ask mode is one-shot (sends a
 *     // query, navigates to the response tab) and the surface that
 *     // would consume a draft doesn't exist.
 *   }
 *
 * Keying by `DraftMode` lets each mode keep its own in-flight draft
 * without clobbering the other (you might switch from Add → Ask to ask
 * a quick clarifying question without losing the long capture you were
 * composing). Per-mode also matches the user-mental-model: the draft
 * belongs to the SURFACE you're typing in, not the route.
 *
 * Failure modes are non-fatal — load returns undefined, save logs and
 * gives up. A corrupt entry, quota exhaustion, or the storage API
 * being unavailable (private mode in some browsers) must never block
 * the user from typing.
 */
import { ConfiguredLogger } from "@/lib/logger";
// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const STORAGE_KEY = "explorer-omnibar-drafts";
/**
 * Maximum size of a single mode's serialized draft (per the rich-text-
 * omnibar plan, mirrors the server's MAX_DOC_BYTES so the client never
 * caches a doc the server would reject). Discards (with a warn log) on
 * save when exceeded — losing the draft is preferable to filling the
 * 5–10MB origin localStorage budget with one runaway capture.
 */
const MAX_DOC_BYTES = 256_000;
const draftStoreLogger = new ConfiguredLogger({ caller: "OmnibarDraftStore" });
// ---------------------------------------------------------------------------
// Read
// ---------------------------------------------------------------------------
function readAll() {
    if (typeof window === "undefined")
        return {};
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);
        if (!raw)
            return {};
        const parsed = JSON.parse(raw);
        // Light sanity check — JSON.parse can produce anything (a stale
        // entry from an older schema version, a hand-edited value, etc.).
        // We only commit to the shape if the top level is a plain object;
        // per-mode entries are validated lazily on `loadDraft`.
        if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
            return {};
        }
        return parsed;
    }
    catch (e) {
        // Per the error-observability rule: log + fall through to a clean
        // default. A corrupt entry never blocks composition.
        draftStoreLogger.warn("Failed to read omnibar drafts from localStorage", {
            error: e instanceof Error ? e.message : String(e),
        });
        return {};
    }
}
function writeAll(drafts) {
    if (typeof window === "undefined")
        return;
    try {
        // Empty-state shortcut: rather than persisting `{}` and growing the
        // origin's storage with a useless entry, drop the key when no mode
        // has a live draft. Re-created on the next save call.
        const hasAny = Object.values(drafts).some((v) => v !== undefined);
        if (!hasAny) {
            window.localStorage.removeItem(STORAGE_KEY);
            return;
        }
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(drafts));
    }
    catch (e) {
        // Most likely cause: QuotaExceededError. Log and move on — the
        // user's NEXT keystroke will trigger another save attempt anyway.
        draftStoreLogger.warn("Failed to save omnibar drafts to localStorage", {
            error: e instanceof Error ? e.message : String(e),
        });
    }
}
/**
 * Load the persisted draft for the given mode, or undefined when no
 * draft exists / the persisted entry doesn't look like a usable
 * Tiptap document.
 *
 * Validation here is intentionally light — we check the entry has a
 * `doc` field with a `type === "doc"` shape so the editor won't choke
 * on it; deeper validation (depth, node count) happens in
 * `TiptapDocumentSchema` when the draft eventually rides into a
 * server submit.
 */
export function loadDraft(mode) {
    const drafts = readAll();
    const entry = drafts[mode];
    if (!entry || typeof entry !== "object")
        return undefined;
    const doc = entry.doc;
    if (!doc || typeof doc !== "object" || doc.type !== "doc") {
        return undefined;
    }
    return doc;
}
// ---------------------------------------------------------------------------
// Write
// ---------------------------------------------------------------------------
/**
 * Persist a draft for the given mode. Caller is responsible for any
 * debouncing — saving on every transaction would dirty the disk on
 * every keystroke (modern SSDs handle it fine, but the reads we'd
 * compete with on heavy use are the more interesting cost).
 *
 * Oversize docs are silently dropped (with a warn log) — past the
 * cap we're either looking at a runaway editor or a paste of a giant
 * document, neither of which fits the omnibar's "quick capture"
 * posture. The user can still submit the draft via the send button
 * (the server enforces its own cap separately); we just don't cache it.
 */
export function saveDraft(mode, doc) {
    // Stringify once so we can both length-check and write the same blob.
    let serialized;
    try {
        serialized = JSON.stringify(doc);
    }
    catch (e) {
        draftStoreLogger.warn("Draft doc is not JSON-serializable; not saving", {
            mode,
            error: e instanceof Error ? e.message : String(e),
        });
        return;
    }
    if (serialized.length > MAX_DOC_BYTES) {
        draftStoreLogger.warn("Draft exceeds MAX_DOC_BYTES; not caching", {
            mode,
            bytes: serialized.length,
            cap: MAX_DOC_BYTES,
        });
        return;
    }
    const drafts = readAll();
    drafts[mode] = { doc, updatedAt: new Date().toISOString() };
    writeAll(drafts);
}
/**
 * Drop the persisted draft for the given mode. Called after a successful
 * submit (so the next bar mount starts fresh) and from the omnibar's
 * minimize callback (so a result-card click teardown doesn't leave
 * stale text in the cache).
 */
export function clearDraft(mode) {
    const drafts = readAll();
    if (drafts[mode] === undefined)
        return;
    delete drafts[mode];
    writeAll(drafts);
}
// ---------------------------------------------------------------------------
// Test surface (only used by tests; tree-shaken from prod bundles since
// `MAX_DOC_BYTES` is unused outside this module).
// ---------------------------------------------------------------------------
/** Exported for tests so they can construct edge-case docs that brush the cap. */
export const __TESTING_MAX_DOC_BYTES = MAX_DOC_BYTES;
